import {
  type ChangeEvent,
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { ArrowUpBlue, ArrowDownGrey, SearchIcon, CheckBlue } from "../../icons";
import {
  InputItem,
  LoadingBar,
  Typography,
  TypographyVariant,
} from "../../ui-kit";

const CheckIcon = styled(CheckBlue)`
  width: 10px;
`;

const DropDown = styled.div`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 22px;

  cursor: pointer;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #dbe3eb;
  border-radius: 5px;
  padding: 6px 10px 6px 12px;
  min-height: 22px;
  position: relative;
  transition: 200ms;
  &:hover {
    border: solid 1px rgba(0, 0, 0, 0);
    box-shadow: 0 0 10px rgba(129, 129, 165, 0.3);
  }
`;

const ValueSelect = styled.span`
  font-family: Montserrat;
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  color: #13273f;
`;

const DropDownListContainer = styled.div<{ fullWidth?: boolean }>`
  position: absolute;
  background-color: #fff;
  top: 43px;
  width: 100%;
  z-index: 11;
  border: 1px solid #eeeeee;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(129, 129, 165, 0.15);
`;

const DropDownList = styled.ul`
  box-sizing: border-box;
  padding: 0;
  background: #fff;
  margin: 3px 0;
  max-height: 250px;
  overflow: auto;
  position: relative;
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #2cd19d80;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 5px;
    background-color: #eeeeee;
  }
`;

const Category = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 10px 12px 12px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f4f7f9;
  }
`;

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const CategoryTitle = styled(Typography)`
  font-style: normal;

  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: #13273f;
`;

const InputSection = styled.div<{ fullWidth?: boolean }>`
  position: relative;
  height: 36px;
  width: 100%;
  align-items: center;
  border-radius: 5px;
`;

const Container = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledTypography = styled(Typography)`
  margin-bottom: 5px;
`;

const SearchInputWrapper = styled.div`
  /* margin: 12px 0; */
  position: sticky;
  top: 0;
  padding: 12px;
  background-color: #fff;
`;

const StyledSearchIcon = styled(SearchIcon)`
  left: 23px;
  top: 20px;
  position: absolute;
`;

const SearchInput = styled.input`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #13273f;

  border: 1px solid #dbe3eb;
  border-radius: 5px;
  padding: 6px 10px 6px 33px;
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &:focus-within {
    border: solid 1px #027aff;
  }

  ::placeholder {
    color: #8181a5;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  span {
    display: inline-flex;
  }
`;

export interface SearchInputDropdownProps {
  items: InputItem[];
  onChange: (value: InputItem) => void;
  fullWidth?: boolean;
  label?: string | JSX.Element;
  externalSearchHandler?: (val?: string) => void;
  loading?: boolean;
  /** Controlled value (item `value`). `""` selects the first ("All …") item; omit to stay uncontrolled. */
  value?: string | number;
}

export const SearchInputDropdown: FC<SearchInputDropdownProps> = ({
  items,
  loading,
  onChange,
  fullWidth = true,
  label,
  externalSearchHandler,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InputItem>({
    label: items?.[0].label,
    value: items?.[0]?.value,
  });

  // Follow the controlled value: restored filters must show the chosen
  // company/user, and a cleared filter must fall back to the "All" item.
  // Items load async, so keep the current label until the match arrives.
  useEffect(() => {
    if (value === undefined) return;
    if (value === null || value === "") {
      if (items?.[0] && selectedItem.value !== items[0].value) {
        setSelectedItem(items[0]);
      }
      return;
    }
    const match = items.find((item) => item.value === value);
    if (match && match.value !== selectedItem.value) {
      setSelectedItem(match);
    }
  }, [value, items]);

  const [searchString, setSearchString] = useState<string>("");
  const [maxHeight, setMaxHeight] = useState(350);
  const ref = useRef<HTMLDivElement>(null);
  const valueRef = useRef(null);

  const updateMaxHeight = () => {
    if (ref.current) {
      const dropDownRect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const distanceToBottom = windowHeight - dropDownRect.bottom - 20;
      setMaxHeight(Math.min(distanceToBottom, 350));
    }
  };

  useEffect(() => {
    const clickOnWindows = (e: MouseEvent) => {
      if (ref.current !== e.target && e.target !== valueRef.current) {
        setIsOpen(false);
        setSearchString("");
        externalSearchHandler?.(undefined);
      }
    };
    window.addEventListener("click", clickOnWindows);

    return () => {
      window.removeEventListener("click", clickOnWindows);
    };
  }, []);

  const handleExpand = () => {
    setIsOpen(!isOpen);
    updateMaxHeight();
    setSearchString("");
    externalSearchHandler?.(undefined);
  };

  const handleItemClick = (item: InputItem) => () => {
    setSelectedItem(item);
    onChange(item);
    setIsOpen(!isOpen);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchString(value);
    externalSearchHandler?.(value);
  };

  // Without an external (server-side) search the list is static, so the
  // type-to-filter box narrows it here. The first item ("All …") always stays.
  const visibleItems = useMemo(() => {
    const q = searchString.trim().toLowerCase();
    if (externalSearchHandler || !q) return items;
    return items.filter(
      (item, index) => index === 0 || item.label.toLowerCase().includes(q)
    );
  }, [items, searchString, externalSearchHandler]);

  return (
    <Container fullWidth={fullWidth}>
      {label && (
        <LabelWrapper>
          <StyledTypography variant={TypographyVariant.HEADER3}>
            {label}
          </StyledTypography>
        </LabelWrapper>
      )}

      <InputSection fullWidth={fullWidth}>
        <DropDown ref={ref} onClick={handleExpand}>
          <ValueSelect ref={valueRef}>
            {selectedItem?.label
              ? selectedItem?.label.length >= 42
                ? `${selectedItem?.label.slice(0, 42)}...`
                : selectedItem?.label
              : ""}
          </ValueSelect>
          {isOpen ? <ArrowUpBlue /> : <ArrowDownGrey />}
        </DropDown>

        {isOpen && (
          <DropDownListContainer
            fullWidth={fullWidth}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <DropDownList style={{ maxHeight }}>
              <SearchInputWrapper>
                <SearchInput
                  value={searchString}
                  onChange={handleSearch}
                  placeholder="Search..."
                />
                <StyledSearchIcon />
              </SearchInputWrapper>

              {loading ? (
                <LoadingBar loading={loading} margin="5px 0" />
              ) : (
                visibleItems.map((item, index) => {
                  return (
                    <div key={item.value ?? index}>
                      <Category onClick={handleItemClick(item)}>
                        <CategoryTitleWrapper>
                          <CategoryTitle>{item.label}</CategoryTitle>
                        </CategoryTitleWrapper>
                        {selectedItem?.value === item?.value && <CheckIcon />}
                      </Category>
                    </div>
                  );
                })
              )}
            </DropDownList>
          </DropDownListContainer>
        )}
      </InputSection>
    </Container>
  );
};
